import React, { useMemo, useRef, useCallback } from 'react';

import {
  useFreecodeContextRun,
  useFreecodeContextData,
  useFreecodeContextLayout,
  useAppContext,
} from 'hooks/useContext';
import { useDetectOutsideClick } from 'hooks/useDetectOutsideClick';
import { useQueryParams } from 'hooks/useQueryParams';
import { editorTypes } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { TitleRefSpan, TestTitle, TestTitleEdit } from './styles';
import FreecodeText from './FreecodeText';
import getComputedDescription from 'functions/getComputedDescription';
import getDescriptionOnCopyDictionary from './getDescriptionOnCopyDictionary';
import services from 'api/services';

const { TicketService } = services;

export default function FreecodeDescription() {
  const { courseId } = useQueryParams();
  const { language: appLanguage } = useAppContext();
  const { editorType } = useFreecodeContextLayout();
  const { ticket, test, setTest, testImpl, setTestImpl } = useFreecodeContextData();
  const { language, levelToRun } = useFreecodeContextRun();

  const titleRef = useRef(null);
  const [editTitle, setEditTitle] = useDetectOutsideClick(titleRef, false);

  const memoizedDescription = useMemo(
    () =>
      getComputedDescription(
        testImpl.candidateDescription,
        test.category,
        language,
        levelToRun ?? 0,
        appLanguage,
        !!courseId
      ),
    [testImpl.candidateDescription, test.category, language, levelToRun, appLanguage, courseId]
  );

  const onCopyDictionary = useMemo(
    () => getDescriptionOnCopyDictionary(testImpl.candidateDescription),
    [testImpl.candidateDescription]
  );

  const updateDescription = useCallback(
    (value) => setTestImpl((oldTestImpl) => ({ ...oldTestImpl, candidateDescription: value })),
    [setTestImpl]
  );

  const updateTitle = (event) => {
    setTest((oldTest) => ({ ...oldTest, name: event.target.value }));
  };

  const getTitleElement = () => (
    <TitleRefSpan ref={titleRef}>
      {editTitle && <TestTitleEdit onChange={updateTitle} value={test.name} />}
      {!editTitle && (
        <TestTitle onClick={() => setEditTitle(editorType === editorTypes.creator)}>{test.name}</TestTitle>
      )}
    </TitleRefSpan>
  );

  const onCopy = (e) => {
    if (editorType !== editorTypes.freecode) return;
    let selection = document.getSelection().toString();
    try {
      TicketService.updateFreecodeCopyEvent(ticket._id, {
        date: new Date(),
        type: 'description',
        data: selection,
      });
    } catch (error) {
      console.error(error);
    }
    for (const { from, to } of onCopyDictionary) selection = selection.replaceAll(from, to);
    e.clipboardData.setData('text/plain', selection);
    e.preventDefault();
  };

  return (
    <FreecodeText
      text={memoizedDescription}
      textRaw={testImpl.candidateDescription}
      textRawEdit={updateDescription}
      titleElement={getTitleElement()}
      onCopy={onCopy}
    />
  );
}
